import { lazy, Suspense, useEffect, useState } from 'react';
import { GAME_UI } from '../app/games';
import { AnimatePresence, motion } from 'motion/react';
import { Bot, Check, Copy, Crown, DoorOpen, Info, Lock, LockOpen, LogOut, Monitor, Play, QrCode, Settings2, Share2, Shuffle, UserX, X } from 'lucide-react';
import { GAME_IDS, type MemberView } from '@gmp/shared-types/browser';
import { Avatar, MediaConsentCard, Sheet, Stage, usePartyUi } from '@gmp/shared-ui';
import { party } from '../net/client';
import { displayUrl, inviteUrl } from '../net/config';
import { gameInfo } from '../app/catalog';
import { sound } from '../app/prefs';
import { navigate } from '../app/router';
import { QR } from '../app/QR';
import { GameCard, GameSections } from './GameCard';
import { HowTo } from './HowTo';
import { GameSettingsForm, RoomSettingsForm } from './SettingsSheets';
import { InviteFriends } from './Friends';
import { SettingsPanel } from './Settings';
import { Championship } from './Results';
import { Mascot } from '../app/Mascot';
import { TurnAlerts } from '../app/TurnAlerts';
import { CastButton } from '../app/cast';
import { SiteFooter } from '../app/Footer';

// Grudge Gauntlet's lobby panel ("Just this one" quick-pick) ships with the game's own screens.
const GauntletLobby = lazy(() => import('@gmp/grudge-gauntlet/ui').then((m) => ({ default: m.GauntletLobby })));

export function Lobby() {
  const { room, me } = usePartyUi();
  const [sheet, setSheet] = useState<null | 'how' | 'games' | 'settings' | 'me' | 'invite' | { member: MemberView }>(null);
  const isHost = room.you.isHost;
  const players = room.members.filter((m) => !m.spectator);
  const spectators = room.members.filter((m) => m.spectator);
  const meMember = room.members.find((m) => m.id === me);
  const g = gameInfo(room.selectedGame);
  const connectedPlayers = players.filter((p) => p.connected).length;
  const tooFew = connectedPlayers < g.minPlayers;
  const tooMany = connectedPlayers > g.maxPlayers;
  // The game's own reason it can't start (e.g. Misquoted! without enough approved quotes for this difficulty).
  const notice = room.startNotice ?? null;

  // Fetch the selected game's screens while people gather, so the start is instant.
  useEffect(() => {
    void GAME_UI[room.selectedGame].preload();
  }, [room.selectedGame]);

  async function start() {
    sound.play('submit');
    const r = await party.host({ cmd: 'start' });
    if (!r.ok) {
      sound.play('error');
      party.notify(r.error ?? 'Could not start', 'bad');
    }
  }

  async function share() {
    const url = inviteUrl(room.code);
    try {
      if (navigator.share) await navigator.share({ title: 'Join my Grudge Match Party', text: `Party code ${room.code}`, url });
      else {
        await navigator.clipboard.writeText(url);
        party.notify('Invite link copied', 'good');
      }
    } catch {
      /* share sheet dismissed */
    }
  }

  return (
    <Stage game={room.selectedGame}>
      <main className="flex-1 flex flex-col gap-3 safe-pad safe-top safe-bottom py-4 max-w-2xl w-full mx-auto">
        <header className="flex items-center justify-between gap-3">
          <button className="text-left min-w-0" onClick={() => setSheet('invite')} aria-label={`Party code ${room.code}. Show invite`}>
            <span className="text-xs uppercase tracking-widest text-mute font-bold">Party code</span>
            {/* Shrinks with the screen so the code and the three buttons always fit on one row. */}
            <span className={`block font-display font-black ${room.code.length <= 5 ? 'text-[clamp(2rem,11vw,3.5rem)]' : 'text-[clamp(1.6rem,8.5vw,3rem)]'} tracking-[0.08em] leading-none tabular-nums`} data-testid="room-code">{room.code}</span>
          </button>
          <div className="flex gap-2 shrink-0">
            <button className="btn btn-sm btn-secondary" onClick={share} aria-label="Share invite"><Share2 size={18} /></button>
            <button className="btn btn-sm btn-secondary" onClick={() => setSheet('invite')} aria-label="Show QR code"><QrCode size={18} /></button>
            <button className="btn btn-sm btn-secondary" onClick={() => setSheet('me')} aria-label="My settings"><Settings2 size={18} /></button>
          </div>
        </header>

        <section aria-label="Selected game" className="grid gap-2">
          <h2 className="sr-only">Up next</h2>
          <GameCard
            id={room.selectedGame}
            lobby
            actions={
              <button className="grid place-items-center size-9 rounded-full bg-black/45 backdrop-blur text-paper ring-1 ring-white/15" onClick={() => setSheet('how')} aria-label={`How to play ${g.name}`} data-testid="how-to-play">
                <Info size={20} />
              </button>
            }
            extra={isHost && (
              <button className="chip text-[11px] font-bold ring-1 ring-[var(--accent)]" onClick={() => setSheet('games')} data-testid="change-game"><Shuffle size={12} /> Change game mode</button>
            )}
          />
          {room.selectedGame === 'grudge-gauntlet' && (
            <Suspense fallback={null}>
              <GauntletLobby s={(room.gameSettings['grudge-gauntlet'] as Record<string, unknown> | undefined) ?? {}} isHost={isHost} onPatch={(patch) => void party.host({ cmd: 'game_settings', gameId: 'grudge-gauntlet', patch }).then((r) => !r.ok && party.notify(r.error ?? 'Not saved', 'bad'))} />
            </Suspense>
          )}
          {room.selectedGame === 'misquoted' && <MisquotedRule s={room.gameSettings.misquoted as { difficulty?: string; rounds?: number } | undefined} />}
          {notice && (
            <p className="card p-3 text-sm border-[var(--accent-2)]" role="status" data-testid="start-notice">{notice}</p>
          )}
          {gameInfo(room.selectedGame).category === 'async' && <TurnAlerts compact />}
        </section>

        {/* Camera games: agree to share photos and clips once, here, instead of on every submission. */}
        {g.usesMedia && room.you.role === 'player' && <MediaConsentCard code={room.code} game={g.name} onAgree={() => sound.play('select')} />}

        <section aria-label="Players" className="card p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="font-display font-black text-lg">Players <span className="text-mute">{players.length}/{room.settings.capacity}</span></h2>
            <div className="flex items-center gap-2">
              {room.locked && <span className="chip"><Lock size={14} /> Locked</span>}
              {isHost && (
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={players.length >= room.settings.capacity}
                  onClick={async () => {
                    // Every AI player is the same (easy) level.
                    const r = await party.host({ cmd: 'add_bot', level: 'easy' });
                    // The new seat's "join" sound comes with the room update (on the TV when there is one).
                    if (!r.ok) party.notify(r.error ?? 'Could not add an AI player', 'bad');
                  }}
                  data-testid="add-bot"
                >
                  <Bot size={16} /> Add AI
                </button>
              )}
            </div>
          </div>
          <ul className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            <AnimatePresence>
              {players.map((m) => (
                <motion.li
                  key={m.id}
                  layout
                  initial={{ scale: 0.3, opacity: 0, rotate: -12 }}
                  animate={{ scale: 1, opacity: m.connected ? 1 : 0.45, rotate: 0 }}
                  exit={{ scale: 0.3, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                >
                  <button
                    className={`w-full flex flex-col items-center gap-0.5 rounded-2xl p-1.5 ${m.id === me ? 'bg-ink-3 ring-2 ring-[var(--accent)]' : ''}`}
                    onClick={() => isHost && m.id !== me && setSheet({ member: m })}
                    aria-label={`${m.nickname}${m.bot ? `, AI player (${m.bot})` : ''}${m.isHost ? ', host' : ''}${m.ready ? ', ready' : ''}${m.connected ? '' : ', reconnecting'}`}
                  >
                    <span className="relative">
                      <Avatar id={m.avatar} size={44} color={m.color} />
                      {m.isHost && <Crown size={16} className="absolute -top-2 -right-1.5 text-brand-2 drop-shadow" />}
                      {m.bot ? (
                        <span className="absolute -bottom-1 -right-1 rounded-full bg-ink-3 ring-2 ring-[var(--accent)] p-0.5"><Bot size={12} /></span>
                      ) : m.ready && <span className="absolute -bottom-1 -right-1 rounded-full bg-good text-ink p-0.5"><Check size={12} strokeWidth={4} /></span>}
                    </span>
                    <span className="text-xs font-bold truncate max-w-full">{m.nickname}</span>
                    {m.bot && <span className="text-[10px] text-mute uppercase tracking-wider leading-none">AI</span>}
                    {!m.connected && <span className="text-[10px] text-mute">reconnecting</span>}
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          {players.length === 1 && (
            <div className="mt-2 flex items-center gap-3">
              <Mascot pose="wave" size={64} />
              <p className="text-mute text-sm">Just you so far! Share the code{isHost ? ', or add AI players to play right now' : ''}.</p>
            </div>
          )}
          {spectators.length > 0 && <p className="text-mute text-sm mt-3">Watching: {spectators.map((s) => s.nickname).join(', ')}</p>}
          {isHost && room.pendingJoins.length > 0 && (
            <div className="mt-4 grid gap-2" aria-label="Join requests">
              {room.pendingJoins.map((p) => (
                <div key={p.id} className="flex items-center gap-3 bg-ink-3 rounded-2xl p-2">
                  <Avatar id={p.avatar} size={36} />
                  <span className="flex-1 font-bold">{p.nickname} wants in</span>
                  <button className="btn btn-sm" onClick={() => party.host({ cmd: 'approve_join', playerId: p.id, approve: true })} aria-label={`Let ${p.nickname} in`}><Check size={16} /></button>
                  <button className="btn btn-sm btn-secondary" onClick={() => party.host({ cmd: 'approve_join', playerId: p.id, approve: false })} aria-label={`Decline ${p.nickname}`}><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </section>


        {room.championship.length > 0 && room.settings.championship && <Championship />}

        <div className="mt-auto grid gap-2 sticky bottom-0 -mx-4 px-4 pt-4 pb-3 bg-gradient-to-t from-[var(--stage)] via-[var(--stage)] to-transparent">
          <div className="flex gap-2">
            {meMember && !meMember.spectator && (
              <button className={`btn btn-duo flex-1 ${meMember.ready ? 'btn-blue' : 'btn-yellow'}`} onClick={() => { party.ready(!meMember.ready); sound.play(meMember.ready ? 'unready' : 'ready', { scope: 'personal' }); }} aria-label={meMember.ready ? 'Ready! Tap to undo' : "I'm ready"} data-testid="ready-toggle">
                {meMember.ready ? <><Check /> Ready!</> : "I'm ready"}
              </button>
            )}
            {isHost && (
              <button className="btn btn-duo btn-green flex-1" disabled={tooFew || tooMany || !!notice} onClick={start} aria-label={tooFew || tooMany || notice ? undefined : `Start ${g.name}`} data-testid="start-game">
                {tooFew || tooMany || notice
                  ? <span className="truncate">{tooFew ? `Need ${g.minPlayers}+ players` : tooMany ? `Max ${g.maxPlayers} players` : 'Can’t start yet'}</span>
                  : <><Play className="shrink-0" /> Start</>}
              </button>
            )}
          </div>
          {isHost ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <button className="btn btn-sm btn-secondary" onClick={() => setSheet('settings')} data-testid="host-settings"><Settings2 size={16} /> Settings</button>
                <button className="btn btn-sm btn-secondary" onClick={() => party.host({ cmd: 'lock', locked: !room.locked })}>{room.locked ? <><LockOpen size={16} /> Open</> : <><Lock size={16} /> Lock</>}</button>
                <button className="btn btn-sm btn-secondary" onClick={() => setSheet('invite')}><Monitor size={16} /> TV</button>
              </div>
              <CastButton displayLink={displayUrl(room.code)} />
            </>
          ) : (
            <>
              <p className="text-center text-mute">Waiting for the host to start…</p>
              {/* The audience link is just the code, so any player can put the party on a TV. */}
              <CastButton displayLink={displayUrl(room.code)} />
            </>
          )}
          <button className="btn btn-ghost btn-sm justify-self-center" onClick={() => { party.leave(); navigate({ name: 'home' }, true); }}>
            <LogOut size={16} /> Leave party
          </button>
        </div>
        <SiteFooter className="-mt-4" />
      </main>

      <Sheet open={sheet === 'how'} onClose={() => setSheet(null)} title={`How to play ${g.name}`}>
        <HowTo gameId={room.selectedGame} />
      </Sheet>
      <Sheet open={sheet === 'games'} onClose={() => setSheet(null)} title="Pick a game">
        <div className="grid gap-3">
          <GameSections compact ids={GAME_IDS} enabled={room.enabledGames} selected={room.selectedGame} card={(id) => (
            <GameCard key={id} id={id} enabled={room.enabledGames.includes(id)} selected={room.selectedGame === id} onClick={() => { void party.host({ cmd: 'select_game', gameId: id }); sound.play('select'); setSheet(null); }} />
          )} />
        </div>
      </Sheet>
      <Sheet open={sheet === 'settings'} onClose={() => setSheet(null)} title="Party settings">
        <div className="grid gap-6">
          <section data-game={room.selectedGame}>
            <h3 className="font-display font-black text-lg mb-3">{g.name}</h3>
            <GameSettingsForm gameId={room.selectedGame} s={room.gameSettings[room.selectedGame] as Record<string, unknown>} playerCount={players.length} />
          </section>
          <hr className="border-line" />
          <section>
            <h3 className="font-display font-black text-lg mb-3">Room</h3>
            <RoomSettingsForm s={room.settings} playerCount={players.length} gameMax={g.maxPlayers} />
          </section>
        </div>
      </Sheet>
      <Sheet open={sheet === 'me'} onClose={() => setSheet(null)} title="My settings">
        <SettingsPanel />
      </Sheet>
      <Sheet open={sheet === 'invite'} onClose={() => setSheet(null)} title="Invite people">
        <InvitePanel />
      </Sheet>
      <Sheet open={typeof sheet === 'object' && sheet !== null} onClose={() => setSheet(null)} title={typeof sheet === 'object' && sheet ? sheet.member.nickname : ''}>
        {typeof sheet === 'object' && sheet && sheet.member.bot ? (
          <div className="grid gap-3">
            <p className="text-mute">A computer player ({sheet.member.bot}). It plays by the same rules as everyone and sees only what its own phone would.</p>
            <button className="btn btn-secondary text-bad" onClick={() => { void party.host({ cmd: 'remove_bot', playerId: sheet.member.id }); setSheet(null); }} data-testid="remove-bot"><UserX size={18} /> Remove AI player</button>
          </div>
        ) : typeof sheet === 'object' && sheet && (
          <div className="grid gap-3">
            <button className="btn btn-secondary" onClick={() => { void party.host({ cmd: 'transfer_host', playerId: sheet.member.id }); setSheet(null); }}><Crown size={18} /> Make host</button>
            <button className="btn btn-secondary text-bad" onClick={() => { void party.host({ cmd: 'kick', playerId: sheet.member.id }); setSheet(null); }} data-testid="kick-player"><UserX size={18} /> Remove from party</button>
          </div>
        )}
      </Sheet>
    </Stage>
  );
}

function InvitePanel() {
  const { room } = usePartyUi();
  const url = inviteUrl(room.code);
  const tv = displayUrl(room.code);
  return (
    <div className="grid gap-5 justify-items-center text-center">
      <QR value={url} size={220} />
      <p className="selectable font-display font-black text-6xl tracking-[0.12em] tabular-nums" data-testid="invite-code">{room.code}</p>
      <p className="selectable text-mute text-sm break-all">{url}</p>
      <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(url).then(() => party.notify('Link copied', 'good'))}><Copy size={16} /> Copy link</button>
      <InviteFriends />
      <div className="card p-4 w-full grid gap-2 text-left">
        <p className="font-display font-black text-lg flex items-center gap-2"><Monitor size={20} /> Audience screen (optional)</p>
        <p className="text-mute text-sm">Open this on a TV or laptop for big-screen reveals. It never shows anyone’s secrets. Remote players don’t need it.</p>
        <p className="selectable text-sm break-all" data-testid="display-link">{tv}</p>
        <div className="flex gap-2 flex-wrap">
          <a className="btn btn-sm" href={tv} target="_blank" rel="noreferrer" data-testid="open-display"><DoorOpen size={16} /> Open audience screen</a>
          <CastButton displayLink={tv} />
          <button className="btn btn-sm btn-secondary" onClick={() => navigator.clipboard?.writeText(tv).then(() => party.notify('Audience link copied', 'good'))}><Copy size={16} /> Copy</button>
        </div>
      </div>
    </div>
  );
}

/** Misquoted!: the exact-wording rule for the chosen difficulty, shown before anyone taps Start. */
function MisquotedRule({ s }: { s: { difficulty?: string; rounds?: number } | undefined }) {
  const d = s?.difficulty ?? 'medium';
  return (
    <p className="card p-3 text-sm" data-testid="mq-lobby-rule">
      <strong className="capitalize">{d}</strong> · {s?.rounds ?? 12} rounds ·{' '}
      {d === 'beginner' ? 'Choose the movie that says this EXACT line.' : 'Choose the movie that says this EXACT line—or choose “Not an actual movie quote.”'}
      {d === 'hard' && ' Any changed word makes it NOT a movie quote.'}
    </p>
  );
}
